/**
 * "Start a new prompt" page (PROC-90).
 *
 * Route: ``/prompts/new``.
 *
 * The entry point for the draft-test-then-PR flow. Small inline form
 * collecting the minimum a ``Prompt`` DB row needs (key + name + org)
 * so the row can exist before any drafting or test-running happens.
 * On submit, creates the row via ``POST /api/basecamp/prompts`` and
 * navigates to ``/prompts/<key>/evaluations`` where the rest of the
 * yaml-only fields (instructions, inputs, sample output, response
 * schema, technical instructions) get drafted and tested.
 *
 * Chosen as a page rather than a modal because the previous
 * ``NewPromptWithPRDialog`` (all-in-one) blocked testing before the
 * PR — putting the setup inline on the same evaluations flow makes
 * the "draft, run tests, then open PR" order visually obvious.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Heading, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useBasecampOrganizationsRetrieve, useCreatePrompt, useListPrompts } from "@api/rest/types-and-hooks";
import type { CreatePromptInput } from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { OrgOwnerSelect } from "@components/Prompts/OrgOwnerSelect";
import { ReportTypeCheckboxes } from "@components/Prompts/ReportTypeTags";
import { GLOBAL_ORG_SENTINEL } from "@components/Prompts/constants";
import { FormInput } from "@components/forms/FormInput";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

// Mirror of the backend regex in ``PromptWriteSerializer`` — enforced
// client-side so the user sees the error inline instead of a round-trip.
const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{1,79}$/;

// Route sentinels that shadow ``/prompts/:key`` sub-routes. Creating a
// Prompt with any of these keys succeeds server-side but leaves the
// prompt orphaned in the UI: the router matches the sentinel route
// before ``:key``, so the detail / evaluations pages become
// unreachable. Reject client-side. If a new reserved segment is added
// to the router, add it here.
const RESERVED_KEYS = new Set(["new"]);

export const NewPrompt: React.FC = () => {
  const navigate = useNavigate();
  const createMutation = useCreatePrompt();
  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({});

  // Same array-vs-object workaround the other prompt forms have to
  // apply — the OpenAPI schema types this as a single object but the
  // API returns an array.
  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );

  // Debounced key value used for the uniqueness lookup. The unlimited
  // ``useListPrompts({ search })`` returns matches via ``icontains`` on
  // key + name, so we filter the results for an exact-key hit before
  // flagging. Debouncing keeps the endpoint from being hit on every
  // keystroke; 300 ms feels responsive without being noisy.
  const [debouncedKey, setDebouncedKey] = useState("");

  const { formState, onSubmit, watch, setValue, register } = useFormUtils<CreatePromptInput>({
    defaultValues: { organization_id: GLOBAL_ORG_SENTINEL, report_types: [] } as Partial<CreatePromptInput>,
    mutationFn: async (params) => {
      if (!KEY_PATTERN.test(params.key ?? "")) {
        notifyError(
          "Prompt key must start with a letter and contain only letters, digits, and underscores (2-80 chars).",
        );
        return;
      }
      if (RESERVED_KEYS.has(params.key ?? "")) {
        notifyError(
          `'${params.key}' is a reserved route segment — the prompt would be unreachable from the UI. Pick a different key.`,
        );
        return;
      }
      if (!params.name?.trim()) {
        notifyError("Prompt name is required.");
        return;
      }
      if (!params.report_types?.length) {
        notifyError("Select at least one report type this prompt is valid for.");
        return;
      }

      const formOrgId = params.organization_id;
      const organization_id = !formOrgId || formOrgId === GLOBAL_ORG_SENTINEL ? null : formOrgId;

      try {
        const result = await createMutation.mutateAsync({
          data: {
            key: params.key,
            name: params.name,
            response_schema: null,
            sample_output: null,
            organization_id,
            report_types: params.report_types,
          },
        });

        if (result.status === 201) {
          notifySuccess("Prompt created — start drafting.");
          navigate(`/prompts/${encodeURIComponent(params.key)}/evaluations`);
        } else {
          const detail = (result.data as { detail?: string } | undefined)?.detail;
          notifyError(detail ?? `Failed to create prompt (status ${result.status}).`);
        }
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Failed to create prompt.");
      }
    },
  });

  // ``organization_id`` is managed via setValue (not native register),
  // so tell react-hook-form the field exists.
  useEffect(() => {
    register("organization_id");
    register("report_types");
  }, [register]);

  // Debounce ``key`` for the uniqueness lookup. Format validity is
  // recomputed inside the timeout so the effect deps stay bounded to
  // the raw input value — earlier version depended on both ``keyValue``
  // AND ``keyIsValidFormat``, which meant every keystroke re-created
  // the effect closure and re-registered the timer. That churn made
  // the ``RegisteredFormInput`` reference (from ``useFormUtils`` /
  // ``useCallback([register, formState])``) rotate faster than React
  // could reconcile it, and the input lost focus after one character.
  const keyValue = watch("key") ?? "";
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedKey(KEY_PATTERN.test(keyValue) ? keyValue : "");
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyValue]);
  const keyIsValidFormat = KEY_PATTERN.test(keyValue);

  const { data: dupSearchResp, isFetching: dupCheckPending } = useListPrompts(
    { search: debouncedKey, page_size: 5 },
    { query: { enabled: !!debouncedKey } },
  );
  // During the 300 ms debounce window, ``keyValue`` (live) and
  // ``debouncedKey`` (delayed) disagree. Rendering "Available." /
  // "already exists" against the live value while the query still
  // reflects the previous keystroke would flash a stale verdict.
  // Only report a result once the live value matches the value we
  // actually queried for.
  const debounceInSync = keyValue === debouncedKey;
  // The search is ``icontains``, so a query of ``wnat`` returns
  // ``wnat_8``, ``wnat_2``, etc. Filter for an exact key match before
  // flagging the input as taken.
  const keyIsTaken =
    !!debouncedKey &&
    debounceInSync &&
    dupSearchResp?.status === 200 &&
    (dupSearchResp.data.results ?? []).some((p) => p.key === debouncedKey);

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
      <Breadcrumb crumbs={[{ label: "Prompts", to: "/prompts" }, { label: "New prompt" }]} />
      {/* Centered content column. The page previously left-aligned
          everything under a ``max-w-2xl`` card which read as unfinished
          against the wide viewport. Wrapping the heading, description,
          and form card in ``mx-auto`` gives the setup step visual
          focus and matches other single-action pages in the app. */}
      <div className="mx-auto w-full max-w-2xl mt-8">
        <div className="text-center mb-6">
          <Heading size="medium">Start a new prompt</Heading>
          <Paragraph size="small" colorType="secondary" className="mt-2">
            Pick a key, name, owning org, and report types. You&apos;ll draft the rest and run tests on the next page.
          </Paragraph>
        </div>

        <div className="rounded-xl border border-border-medium bg-bg-surface p-6 shadow-sm">
          <FormContainer onSubmit={onSubmit}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-text-secondary">
                  Prompt key <span className="text-text-error-primary">*</span>
                  <span className="ml-2 text-xs font-normal text-text-tertiary">
                    letters and underscores (e.g. <code>my_new_prompt</code>)
                  </span>
                </span>
                {/* Rendered against the stable ``FormInput`` import
                    (module-level, never changes) instead of the
                    ``RegisteredFormInput`` returned by ``useFormUtils``.
                    That helper rewraps ``FormInput`` in a ``useCallback``
                    with ``formState`` as a dep — every keystroke changes
                    ``formState``, so the wrapper's identity rotates and
                    React unmounts + remounts the underlying input,
                    losing focus. Rendering ``FormInput`` directly keeps
                    the component identity stable across renders. */}
                <FormInput<CreatePromptInput>
                  register={register}
                  formState={formState}
                  formKey="key"
                  required
                  placeholder="my_new_prompt"
                />
                {/* Live uniqueness feedback. Client-side format check
                    handles bad chars/length; the debounced backend
                    query catches collisions with an already-existing
                    ``Prompt.key`` (either in this env or committed to
                    yaml). Server still enforces on submit — this is
                    for early UX, not authority. */}
                {keyValue && !keyIsValidFormat && (
                  <span className="text-xs text-text-error-primary">
                    Must start with a letter and contain only letters, digits, and underscores (2–80 chars).
                  </span>
                )}
                {keyIsValidFormat && RESERVED_KEYS.has(keyValue) && (
                  <span className="text-xs text-text-error-primary">
                    '{keyValue}' is a reserved route — the prompt would be unreachable. Pick a different key.
                  </span>
                )}
                {keyIsValidFormat && !RESERVED_KEYS.has(keyValue) && (dupCheckPending || !debounceInSync) && (
                  <span className="text-xs text-text-tertiary">Checking availability…</span>
                )}
                {keyIsTaken && (
                  <span className="text-xs text-text-error-primary">
                    A prompt with that key already exists. Pick a different key.
                  </span>
                )}
                {keyIsValidFormat &&
                  !RESERVED_KEYS.has(keyValue) &&
                  debounceInSync &&
                  !dupCheckPending &&
                  !keyIsTaken &&
                  debouncedKey && <span className="text-xs text-emerald-700">Available.</span>}
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-text-secondary">
                  Display name <span className="text-text-error-primary">*</span>
                </span>
                <FormInput<CreatePromptInput>
                  register={register}
                  formState={formState}
                  formKey="name"
                  required
                  placeholder="My New Prompt"
                />
              </div>

              <OrgOwnerSelect
                orgs={orgs}
                value={watch("organization_id") as string | undefined}
                onChange={(v) => setValue("organization_id", v)}
              />

              <ReportTypeCheckboxes value={watch("report_types") ?? []} onChange={(v) => setValue("report_types", v)} />

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/prompts")}
                  className="text-sm text-text-secondary hover:text-text-primary underline"
                >
                  Cancel
                </button>
                <Button
                  text={createMutation.isPending ? "Creating…" : "Start drafting →"}
                  appearance="primary"
                  onClick={onSubmit}
                  disabled={
                    createMutation.isPending || formState.isSubmitting || keyIsTaken || RESERVED_KEYS.has(keyValue)
                  }
                />
              </div>
            </div>
          </FormContainer>
        </div>
      </div>
    </div>
  );
};
